import { Injectable, signal, WritableSignal } from "@angular/core";
import { Customer } from "../customer/shared/customer.model";
import { DealerRep } from "../dealer-rep/shared/dealer-rep.model";
import { Dealer } from "../dealer/shared/dealer.model";
import { DroneModel } from "../drone/shared/drone-model.model";
import { Employee } from "../employee/shared/employee.model";
import { Folder } from "../folder/folder.model";
import { IamRole } from "../iam/iam-role.model";
import { Country } from "../postal/country.model";
import { BaseUser } from "../user/base-user.model";
import { UnknownUser } from "../user/unknown-user.model";
import { AddCustomerState, AddDealerRepState, AddDealerState, AddDroneState, AddEmployeeState, TransferDroneState, UpdateCustomerState, UpdateDealerRepState, UpdateDealerState, UpdateDroneState, UpdateEmployeeState, ViewCustomersState, ViewDealerRepsState, ViewDealersState, ViewDronesState, ViewEmployeesState, ViewLogsState } from "./state-interface";
import { UserType } from "./user-type.model";

@Injectable({
    providedIn: 'root'
})

export class StateManager {

    public viewEmployeesState: ViewEmployeesState | undefined;
    public addEmployeeState: AddEmployeeState | undefined;
    public updateEmployeeState: UpdateEmployeeState | undefined;

    public viewDealersState: ViewDealersState | undefined;
    public addDealerState: AddDealerState | undefined;
    public updateDealerState: UpdateDealerState | undefined;

    public viewDealerRepsState: ViewDealerRepsState | undefined;
    public addDealerRepState: AddDealerRepState | undefined;
    public updateDealerRepState: UpdateDealerRepState | undefined;

    public viewCustomersState: ViewCustomersState | undefined;
    public addCustomerState: AddCustomerState | undefined;
    public updateCustomerState: UpdateCustomerState | undefined;

    public viewDronesState: ViewDronesState | undefined;
    public addDroneState: AddDroneState | undefined;
    public updateDroneState: UpdateDroneState | undefined;
    public transferDroneState: TransferDroneState | undefined;

    public viewLogsState: ViewLogsState | undefined;

    public employeeNames: Employee[] | undefined;
    public droneModels: DroneModel[] | undefined;
    public countries: Country[] | undefined;
    public transferToTypes: UserType[] | undefined;
    public uploadedByTypes: UserType[] | undefined;
    
    private _dealerNames: {[key: string]: Dealer[]} = {};
    private _customerNames: {[key: string]: Customer[]} = {};

    private _iamRoles: {[key: string]: IamRole[]} = {};

    private _userAccount: BaseUser | undefined;
    private _userType: string | undefined;

    private _firmwareFolders: WritableSignal<Folder[]> = signal<Folder[]>([]);
    
    public constructor() {
        // Do no rely on constructor being called. If user logs out, then
        // logs in from the same brower tab without going to any other web
        // site, then the constructor will not be called, as the injected
        // instance of this class will still be held in app context.
    }

    public setIamRoles(userType: string, iamRoles: IamRole[]) {
        this._iamRoles[userType] = iamRoles;
    }

    public getIamRoles(userType: string): IamRole[] | undefined {
        return this._iamRoles[userType];
    }

    public getUserAccount(): BaseUser {
        if (this._userAccount == undefined) {
            this._userAccount = new UnknownUser();
        }

        return this._userAccount;
    }

    public setUserAccount(userAccount: BaseUser) {
        this._userAccount = userAccount;

        this._userType = userAccount.constructor.name;

        if (this._userType.charAt(0) == "_") {
            this._userType = this._userType.substring(1);
        }
    }

    public getUserType():string {
        const userAccount = this.getUserAccount();

        if (userAccount instanceof(Employee)) {
            this._userType = "employee";

        } else if (userAccount instanceof(Dealer)) {
            this._userType = "dealer";
        } else if (userAccount instanceof(DealerRep)) {
            this._userType = "dealer-rep";
        } else if (userAccount instanceof(Customer)) {
            this._userType = "customer";
        } else if (userAccount instanceof(UnknownUser)) {
            this._userType = "unknown";
        } else {
            console.error("Invalid user account type");
            this._userType = "invalid";
        }

        return this._userType;
    }

    public setFirmwareFolders(value: Folder[]) {
        return this._firmwareFolders.set(value);
    }

    public getFirmwareFolders(): Folder[] {
        return this._firmwareFolders();
    }

    public getDealerNames(invokingContext?: string): Dealer[] | undefined {
        return this._dealerNames[this.getContext(invokingContext)];
    }

    public setDealerNames(dealerNames: Dealer[], invokingContext?: string) {
        this._dealerNames[this.getContext(invokingContext)] = dealerNames;
    }

    public clearDealerNames() {
        this._dealerNames = {};
    }

    public getCustomerNames(invokingContext?: string): Customer[] | undefined {
        return this._customerNames[this.getContext(invokingContext)];
    }

    public setCustomerNames(customerNames: Customer[], invokingContext?: string) {
        this._customerNames[this.getContext(invokingContext)] = customerNames;
    }

    public clearCustomerNames() {
        this._customerNames = {};
    }

    public reset(): void {
        // Iterate through properties and set those ending with 'State' to undefined.
        for (const key of Object.keys(this)) {
            if (key.endsWith('State')) {
                (this as any)[key] = undefined;
            }
        }

        // Iterate through properties and set those ending with 'Names' to undefined.
        for (const key of Object.keys(this)) {
            if (key.endsWith('Names')) {
                (this as any)[key] = undefined;
            }
        }

        // Set all remaining properties to undefined.

        this.droneModels = undefined;
        this.countries = undefined;
        this.transferToTypes = undefined;
        this.uploadedByTypes = undefined;
        
        this._iamRoles = {};
    
        this._userAccount = undefined;
        this._userType = undefined;
        this._firmwareFolders.set(<Folder[]>([]));
        this._dealerNames = {};
        this._customerNames = {};
    }

    private getContext(invokingContext: string | undefined): string {
        return (invokingContext !== undefined ? invokingContext : "default");
    }
}
